import declare from "dojo/_base/declare";
import html from "csi/html";
import locale from "csi/locale";
import RightMenuAccordionFilter from "csi/widget/_svg/RightMenuAccordionFilter";
import _TemplatedMixin from "dijit/_TemplatedMixin";
import _WidgetBase from "dijit/_WidgetBase";
import _WidgetsInTemplateMixin from "dijit/_WidgetsInTemplateMixin";
import TextBox from "dijit/form/TextBox";
import _RightMenuAccordionMixin from "./_RightMenuAccordionMixin";
import MiniAdHocFilterContainer from "./MiniAdHocFilterContainer";
import template from "!!raw-loader!./resources/RightMenuAccordionItem.html";

const RightMenuAccordionItem = declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin, _RightMenuAccordionMixin], {
	templateString: template,

	/** @property {boolean} accordionExpanded - flag indicating expansion of accordion */
	accordionExpanded: null,

	/** @property {RightMenuAccordionFilter} accordionFilter - filter widget */
	accordionFilter: null,

	/** @property {MiniAdHocFilterContainer} accordionAdHocFilter - adhoc filter widget */
	accordionAdHocFilter: null,

	/** @property {DOMNode} adHocFiltersNode - ad hoc filters node */
	adHocFiltersNode: null,

	/** @property {function} applyFilterCallback - function to call after clicking apply on filter */
	applyFilterCallback: null,

	/** @property {DOMNode} arrowNode - arrow node in the header of accordion container */
	arrowNode: null,

	/** @property {DOMNode} containerNode - container node for accordion */
	containerNode: null,

	/** @property {DOMNode} contentNode - accordion content node  */
	contentNode: null,

	/** @property {DOMNode} filterAttributesNode - filter attributes container node */
	filterAttributesNode: null,

	/** @property {DOMNode} filterAttributesWidgetNode - filter attributes widget node */
	filterAttributesWidgetNode: null,

	/** @property {Spec[]} filterSpecs - filter specs */
	filterSpecs: null,

	/** @property {Query} originalQuery - original store query used in store */
	originalQuery: null,

	/** @property {string} placeholderType - placeholderType for placeholder accordions */
	placeholderType: null,

	/** @property {DOMNode} separatorNode - separator node (outline-bottom work-around)  */
	separatorNode: null,

	/** @property {function} searchCallback - function to call after searching */
	searchCallback: null,

	/** @property {TextBox} textBox - text box to use for searching  */
	textBox: null,

	/** @property {DOMNode} textBoxNode - node for the text box  */
	textBoxNode: null,

	/** @property {string} title - title text on the accordion header */
	title: null,

	/** @property {DOMNode} titleNode - title node containing the accordion's title, totalTextnode, and chevron  */
	titleNode: null,

	/** @property {DOMNode} toggleFilterButtonNode - node for the filter button */
	toggleFilterButtonNode: null,

	/** @property {DOMNode} totalTextNode - text node of number that shows how many children in the accordion container */
	totalTextNode: null,

	/** Click event handler for this accordion */
	_onClick() {
		const {
			accordionExpanded,
		} = this;
		this.expandAccordion(this, !accordionExpanded);
	},

	/** On change for the content of the text box, invoke searchCallback */
	_onFilterChange() {
		this.searchCallback?.(this.textBox.value);
	},

	/** toggle visibility for filter attributes widget */
	toggleFilter() {
		const {
			accordionFilter,
			filterAttributesNode,
		} = this;

		if (filterAttributesNode.classList.contains(html.CSS_HIDE)) {
			filterAttributesNode.classList.remove(html.CSS_HIDE);
		} else {
			accordionFilter.resetFilterPanel();
			accordionFilter.hideFilterPanel();
		}
	},

	/**
	 * @override
	 * On this life cycle hook, set the total number of children,
	 * append the content node, set the accordion to reflect expand property,
	 * and add text box and filter as needed.
	 */
	postCreate() {
		const {
			adHocFiltersNode,
			applyFilterCallback,
			containerNode,
			contentNode,
			filterAttributesWidgetNode,
			separatorNode,
			toggleFilterButtonNode,
			textBoxNode,
		} = this;

		let searchBoxWidth = 213;
		// show filter if a callback to applyFilter exists
		if (applyFilterCallback) {
			const { domNode } = toggleFilterButtonNode;
			domNode.classList.remove(html.CSS_HIDE);
			searchBoxWidth = 171;

			this.accordionFilter = new RightMenuAccordionFilter({
				accordionItem: this,
			})
				.placeAt(filterAttributesWidgetNode);

			this.accordionAdHocFilter = new MiniAdHocFilterContainer({
				accordionItem: this,
				showClearFiltersButton: true,
			})
				.placeAt(adHocFiltersNode);
		}

		const placeHolderString = locale.resource("Search");

		if (this.searchCallback) {
			this.textBox = new TextBox({
				style: `width: ${searchBoxWidth}px`,
				intermediateChanges: true,
				onChange: (): void => this._onFilterChange(),
				placeHolder: placeHolderString,
			})
				.placeAt(textBoxNode);
		}

		containerNode.insertBefore(contentNode, separatorNode);

		this.inherited(arguments);
	},

	/** Set total text node */
	setTotal(total?: number) {
		const { contentNode: { style }, totalTextNode } = this;
		if (total) {
			style.transition = "max-height 400ms ease-out";
			totalTextNode.innerText = total;
		} else {
			style.transition = "";
			totalTextNode.innerText = 0;
		}
	},
});

export = RightMenuAccordionItem;